/**
 * Phase 14 — bulk-import-team-members modal.
 *
 * Two-phase: dry_run preview → confirm import. Hits
 * `POST /api/admin/team-members/bulk-import`.
 */
import React, { useEffect, useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { describeApiError } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BulkImportTeamMembersDialog({
  open,
  onOpenChange,
  onImportComplete,
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // dry-run response
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [onConflict, setOnConflict] = useState("skip");

  useEffect(() => {
    if (!open) return;
    api
      .get("/admin/team-members/bulk-import/history")
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
    }
  }, [open]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const choose = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
  };

  const runDryRun = async () => {
    if (!file) return;
    setPreviewing(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "true");
    fd.append("on_conflict", onConflict);
    try {
      const { data } = await api.post(
        "/admin/team-members/bulk-import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file || !preview) return;
    setCommitting(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "false");
    fd.append("on_conflict", onConflict);
    try {
      const { data } = await api.post(
        "/admin/team-members/bulk-import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const created = data.rows_imported ?? 0;
      const updated = data.rows_updated ?? 0;
      const preErrs = (data.errors || []).length;
      const applyErrs = (data.apply_errors || []).length;
      const totalErrs = preErrs + applyErrs;
      toast.success(
        `Imported ${created} new · updated ${updated} · ${totalErrs} error${
          totalErrs === 1 ? "" : "s"
        }.`,
      );
      onImportComplete?.(data);
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const sampleHref = `${BACKEND}/api/admin/team-members/bulk-import/sample.csv`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        data-testid="bulk-import-tm-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Bulk-import team members
            </span>
          </DialogTitle>
          <DialogDescription>
            Upload a CSV with the columns{" "}
            <code className="font-mono text-[11px]">
              position_id, full_name, department, service_line
            </code>{" "}
            (required) plus optional partner email, manager info, and hire date.
            <strong className="ml-1">Dates must be MM/DD/YYYY</strong>
            {" "}(e.g. <code className="font-mono text-[11px]">03/15/2026</code>;
            ISO YYYY-MM-DD is still accepted). Up to 5,000 rows per file.
            Legacy CSVs with a{" "}
            <code className="font-mono text-[11px]">file_number</code> column
            are still accepted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="rounded-lg p-3 text-xs"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
              color: "var(--page-fg-secondary)",
            }}
          >
            <a
              href={sampleHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 underline"
              data-testid="bulk-import-tm-sample-link"
              style={{ color: "var(--page-fg)" }}
              onClick={(e) => {
                // Preserve auth — fetch via api so the bearer header goes
                e.preventDefault();
                api
                  .get("/admin/team-members/bulk-import/sample.csv", {
                    responseType: "blob",
                  })
                  .then((res) => {
                    const blob = new Blob([res.data], { type: "text/csv" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "team_members_sample.csv";
                    a.click();
                    window.URL.revokeObjectURL(url);
                  })
                  .catch(() => toast.error("Couldn't download the sample CSV."));
              }}
            >
              <Download className="h-3 w-3" />
              Download sample CSV
            </a>
          </div>

          <div>
            <label
              className="mb-1.5 block text-xs font-medium"
              htmlFor="bulk-tm-file"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              CSV file
            </label>
            <input
              ref={fileInputRef}
              id="bulk-tm-file"
              type="file"
              accept=".csv,text/csv"
              onChange={choose}
              data-testid="bulk-import-tm-file"
              className="block w-full text-xs"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span style={{ color: "var(--page-fg-secondary)" }}>
              On existing position_id:
            </span>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="onConflict"
                value="skip"
                checked={onConflict === "skip"}
                onChange={() => setOnConflict("skip")}
                data-testid="bulk-import-tm-conflict-skip"
              />
              skip
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="onConflict"
                value="update"
                checked={onConflict === "update"}
                onChange={() => setOnConflict("update")}
                data-testid="bulk-import-tm-conflict-update"
              />
              update in place
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={runDryRun}
              disabled={!file || previewing}
              data-testid="bulk-import-tm-preview-btn"
              className="ml-auto"
            >
              {previewing ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1 h-3.5 w-3.5" />
              )}
              Preview (dry run)
            </Button>
          </div>

          {preview && (
            <div
              className="rounded-lg p-3"
              style={{
                backgroundColor: "var(--surface)",
                border: "1px solid var(--border-subtle)",
              }}
              data-testid="bulk-import-tm-preview"
            >
              <p className="text-xs font-semibold" style={{ color: "var(--page-fg)" }}>
                Preview · {preview.filename}
              </p>
              <div
                className="mt-2 grid grid-cols-2 gap-2 text-[11px] tabular-nums sm:grid-cols-4"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                <Stat label="Total rows" value={preview.total_rows} />
                <Stat
                  label="Will create"
                  value={preview.would_create}
                  accent="success"
                />
                <Stat
                  label="Will update"
                  value={preview.would_update}
                  accent="warning"
                />
                <Stat
                  label="Will skip"
                  value={preview.would_skip}
                  accent="muted"
                />
              </div>
              {(preview.errors || []).length > 0 && (
                <div className="mt-3">
                  <p
                    className="inline-flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: "var(--danger-fg)" }}
                  >
                    <AlertCircle className="h-3 w-3" />
                    {preview.errors.length} row error
                    {preview.errors.length === 1 ? "" : "s"}
                  </p>
                  <ul
                    className="mt-1.5 max-h-32 space-y-0.5 overflow-y-auto rounded border px-2 py-1.5 text-[11px]"
                    style={{
                      borderColor: "var(--border-subtle)",
                      color: "var(--page-fg-secondary)",
                    }}
                    data-testid="bulk-import-tm-errors"
                  >
                    {preview.errors.map((e, i) => (
                      <li key={i}>
                        <span
                          className="font-mono"
                          style={{ color: "var(--page-fg)" }}
                        >
                          row {e.row}
                        </span>
                        : {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {history.length > 0 && (
            <details
              className="rounded-lg p-2 text-[11px]"
              style={{
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <summary
                className="cursor-pointer text-xs font-semibold"
                style={{ color: "var(--page-fg)" }}
              >
                Import history (last {history.length})
              </summary>
              <ul className="mt-2 space-y-1" data-testid="bulk-import-tm-history">
                {history.map((h, i) => (
                  <li
                    key={h.id || i}
                    className="flex flex-wrap items-center justify-between gap-2"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    <span style={{ color: "var(--page-fg)" }}>
                      {h.filename || "(unnamed)"}
                    </span>
                    <span className="tabular-nums">
                      +{h.rows_imported || 0} ✏{h.rows_updated || 0} ⏭{h.rows_skipped || 0} ✕
                      {h.rows_failed || 0}
                    </span>
                    <span>
                      {h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-import-tm-close"
          >
            Close
          </Button>
          <Button
            disabled={
              !preview ||
              committing ||
              (preview &&
                preview.would_create === 0 &&
                preview.would_update === 0)
            }
            onClick={commit}
            data-testid="bulk-import-tm-confirm"
            className="goodwin-primary-btn text-white"
          >
            {committing ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1 h-4 w-4" />
            )}
            Confirm import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  const accentMap = {
    success: "var(--success-fg)",
    warning: "var(--warning-fg)",
    muted: "var(--page-fg-muted)",
  };
  return (
    <div
      className="rounded-md px-2 py-1.5"
      style={{
        backgroundColor: "var(--surface-2)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-[0.10em]"
        style={{ color: "var(--page-fg-muted)" }}
      >
        {label}
      </p>
      <p
        className="mt-0.5 font-serif text-base font-semibold"
        style={{ color: accent ? accentMap[accent] : "var(--page-fg)" }}
      >
        {value ?? 0}
      </p>
    </div>
  );
}
